/* eslint-disable no-console */
import React, { useEffect, useRef, useState } from 'react';
import { EdgeLabelRenderer, useStore } from 'reactflow';

// Sample points along the path using getPointAtLength (coordinates are already in world space)
function samplePath(pathEl, zoom = 1) {
  // Higher density sampling reduces risk of missing intersections.
  const d = pathEl.getAttribute('d');
  if (!d) return [];
  const total = pathEl.getTotalLength();
  const zoomFactor = Math.min(3, Math.max(0.4, zoom));
  const targetSamples = 120; // previously 60
  const baseStep = total / targetSamples;
  const step = Math.min(Math.max(2, baseStep / zoomFactor), 25);
  const points = [];
  for (let l = 0; l <= total; l += step) {
    const pt = pathEl.getPointAtLength(l);
    points.push({ x: pt.x, y: pt.y });
  }
  // Ensure final point included.
  if (points.length) {
    const endPt = pathEl.getPointAtLength(total);
    const last = points[points.length - 1];
    if (Math.hypot(endPt.x - last.x, endPt.y - last.y) > 0.5) {
      points.push({ x: endPt.x, y: endPt.y });
    }
  }
  return points;
}

// Detect intersections using a spatial hash to reduce pair checks.
// edgeSamples: array of { id, points, box }
function detectIntersectionsFromSegments(segments, segBoxes, segMeta) {
  const intersections = [];
  if (!segments.length) return intersections;

  // Spatial hash grid
  const cellSize = 48; // px
  const hash = new Map(); // key -> array of seg indices
  const keyFor = (cx, cy) => `${cx}:${cy}`;
  const cellRangeForBox = (b) => {
    const minCx = Math.floor(b.minX / cellSize);
    const maxCx = Math.floor(b.maxX / cellSize);
    const minCy = Math.floor(b.minY / cellSize);
    const maxCy = Math.floor(b.maxY / cellSize);
    return { minCx, maxCx, minCy, maxCy };
  };
  for (let i = 0; i < segments.length; i += 1) {
    const r = cellRangeForBox(segBoxes[i]);
    for (let cx = r.minCx; cx <= r.maxCx; cx += 1) {
      for (let cy = r.minCy; cy <= r.maxCy; cy += 1) {
        const key = keyFor(cx, cy);
        const arr = hash.get(key);
        if (arr) {
          arr.push(i);
        } else {
          hash.set(key, [i]);
        }
      }
    }
  }

  // We now ONLY support strict cross-shaped intersections: horizontal vs vertical segments.
  // Classify segments using a tolerance and then test interior interval overlap (exclude endpoint 'L' corners).
  const axisTol = 0.5; // px tolerance to consider a segment axis-aligned
  const horizontals = []; // { idx, edgeId, y, x1, x2 }
  const verticals = [];   // { idx, edgeId, x, y1, y2 }
  for (let s = 0; s < segments.length; s += 1) {
    const [p1, p2] = segments[s];
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const adx = Math.abs(dx);
    const ady = Math.abs(dy);
    if (ady <= axisTol && adx > 0) { // horizontal
      const y = (p1.y + p2.y) / 2;
      const x1 = Math.min(p1.x, p2.x);
      const x2 = Math.max(p1.x, p2.x);
      horizontals.push({ idx: s, edgeId: segMeta[s].edgeId, y, x1, x2 });
    } else if (adx <= axisTol && ady > 0) { // vertical
      const x = (p1.x + p2.x) / 2;
      const y1 = Math.min(p1.y, p2.y);
      const y2 = Math.max(p1.y, p2.y);
      verticals.push({ idx: s, edgeId: segMeta[s].edgeId, x, y1, y2 });
    }
  }

  if (horizontals.length && verticals.length) {
    // Bucket horizontals by quantized y to reduce comparisons.
    const bucketSize = 10; // px
    const hBuckets = new Map();
    horizontals.forEach(h => {
      const key = Math.round(h.y / bucketSize);
      const arr = hBuckets.get(key);
      if (arr) arr.push(h); else hBuckets.set(key, [h]);
    });
    const nearExisting = (x, y) => {
      for (let i = 0; i < intersections.length; i += 1) {
        if (Math.hypot(intersections[i].x - x, intersections[i].y - y) < 3) return true;
      }
      return false;
    };
    verticals.forEach(v => {
      // Search neighboring y buckets since y may not align exactly.
      const baseKey = Math.round(((v.y1 + v.y2) / 2) / bucketSize);
      for (let key = baseKey - 2; key <= baseKey + 2; key += 1) {
        const hs = hBuckets.get(key);
        if (hs) {
          const { x: vx, y1: vy1, y2: vy2, edgeId: vEdge } = v;
          hs.forEach(h => {
            const { y: hy, x1: hx1, x2: hx2, edgeId: hEdge } = h;
            // Strict interior cross intersection candidate at (vx, hy)
            // Require intersection to be STRICTLY inside both segments (no touching endpoints to avoid L-corners)
            if (hx1 < vx && hx2 > vx && vy1 < hy && vy2 > hy && vEdge !== hEdge) {
              if (!nearExisting(vx, hy)) {
                intersections.push({
                  x: vx,
                  y: hy,
                  edges: [vEdge, hEdge],
                  angles: { [vEdge]: Math.PI / 2, [hEdge]: 0 }
                });
              }
            }
          });
        }
      }
    });
  }
  // Final filter (defensive): ensure exactly two edges and one horizontal/one vertical by angle.
  const angleTol = 0.2; // radians tolerance (~11.5 deg)
  const norm = (a) => {
    let r = a;
    while (r <= -Math.PI) r += 2 * Math.PI;
    while (r > Math.PI) r -= 2 * Math.PI;
    return r;
  };
  const isHorizontal = (a) => {
    const na = Math.abs(norm(a));
    return na < angleTol || Math.abs(na - Math.PI) < angleTol;
  };
  const isVertical = (a) => {
    const na = Math.abs(norm(a));
    return Math.abs(na - Math.PI / 2) < angleTol || Math.abs(na - 3 * Math.PI / 2) < angleTol; // second case for wrapping
  };
  return intersections.filter(pt => {
    if (!pt.edges || pt.edges.length !== 2) return false;
    const angs = pt.angles || {};
    const aVals = pt.edges.map(eid => angs[eid]).filter(a => typeof a === 'number');
    if (aVals.length !== 2) return false;
    const hCount = aVals.reduce((acc, a) => acc + (isHorizontal(a) ? 1 : 0), 0);
    const vCount = aVals.reduce((acc, a) => acc + (isVertical(a) ? 1 : 0), 0);
    return hCount === 1 && vCount === 1;
  });
}

// Debounce helper
function useDebouncedEffect(effect, deps, delay) {
  const timeoutRef = useRef();
  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(effect, delay);
    return () => timeoutRef.current && clearTimeout(timeoutRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

const EdgeIntersections = () => {
  const [points, setPoints] = useState([]);
  // Subscribe to edges, nodes, and viewport transform so we recompute on any layout/path change
  const edges = useStore(state => state.edges);
  const nodes = useStore(state => state.nodes);
  const transform = useStore(state => state.transform); // [x, y, zoom]
  const dragging = useStore(state => state.dragging); // boolean in ReactFlow store
  const rafRef = useRef();
  const scheduledRef = useRef(false);
  const cacheRef = useRef(new Map()); // id -> { d, points, box }

  // Helpers
  const getEdgeId = (el) => {
    if (!el) return null;
    // Prefer closest ancestor with data-id (works across React Flow versions)
    const host = el.closest ? el.closest('[data-id]') : null;
    if (host && host.getAttribute) return host.getAttribute('data-id');
    // Fallback to walking up a few parent levels
    let cur = el.parentElement;
    for (let i = 0; i < 4 && cur; i += 1) {
      const attr = cur.getAttribute ? cur.getAttribute('data-id') : null;
      if (attr) return attr;
      cur = cur.parentElement;
    }
    return null;
  };

  // Stable shallow comparison to avoid unnecessary re-render
  const isSamePoints = (a, b) => {
    if (a === b) return true;
    if (!a || !b || a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      const da = a[i];
      const db = b[i];
      if (Math.abs(da.x - db.x) > 0.5 || Math.abs(da.y - db.y) > 0.5) return false;
    }
    return true;
  };

  const recompute = () => {
    // Prefer the standard React Flow edge path class; fallback to generic selector
    const edgePaths = Array.from(document.querySelectorAll('path.react-flow__edge-path')).length
      ? Array.from(document.querySelectorAll('path.react-flow__edge-path'))
      : Array.from(document.querySelectorAll('.react-flow__edge path'));
    const zm = Array.isArray(transform) ? (transform[2] || 1) : 1;
    // Cache by edge id and `d` attribute
    const seenIds = new Set();
    edgePaths.forEach((p) => {
      const d = p.getAttribute('d') || '';
      const id = getEdgeId(p) || (d ? `d:${d}` : null);
      if (!id) { return; }
      seenIds.add(id);
      const cached = cacheRef.current.get(id);
      if (!cached || cached.d !== d) {
        const pts = samplePath(p, zm);
        if (!pts || pts.length < 2) {
          cacheRef.current.delete(id);
          return;
        }
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        pts.forEach((pt) => {
          if (pt.x < minX) minX = pt.x;
          if (pt.y < minY) minY = pt.y;
          if (pt.x > maxX) maxX = pt.x;
          if (pt.y > maxY) maxY = pt.y;
        });
        cacheRef.current.set(id, { d, points: pts, box: { minX, maxX, minY, maxY } });
      }
    });
    // Purge removed edges
    cacheRef.current.forEach((val, key) => {
      // Only purge entries if we actually scanned any paths this cycle; prevents wiping cache
      if (seenIds.size > 0 && !seenIds.has(key)) {
        cacheRef.current.delete(key);
      }
    });
    // Build or reuse cached per-edge segment metadata to avoid repeating work.
    // cacheRef: id -> { d, points, box, segments?, segBoxes?, segMeta? }
    cacheRef.current.forEach((entry, id) => {
      if (!entry.segments && entry.points && entry.points.length >= 2) {
        const segs = [];
        const boxes = [];
        const meta = [];
        const pts = entry.points;
        for (let k = 0; k < pts.length - 1; k += 1) {
          const a = pts[k];
          const b = pts[k + 1];
          segs.push([a, b]);
          const minX = Math.min(a.x, b.x);
          const maxX = Math.max(a.x, b.x);
          const minY = Math.min(a.y, b.y);
          const maxY = Math.max(a.y, b.y);
          boxes.push({ minX, maxX, minY, maxY });
          meta.push({ edgeId: id, angle: Math.atan2(b.y - a.y, b.x - a.x) });
        }
        entry.segments = segs;
        entry.segBoxes = boxes;
        entry.segMeta = meta;
      }
    });
    // Flatten collected segments for intersection testing.
    const allSegments = [];
    const allSegBoxes = [];
    const allSegMeta = [];
    cacheRef.current.forEach((entry) => {
      if (entry.segments) {
        allSegments.push(...entry.segments);
        allSegBoxes.push(...entry.segBoxes);
        allSegMeta.push(...entry.segMeta);
      }
    });
    const ints = detectIntersectionsFromSegments(allSegments, allSegBoxes, allSegMeta);
    // Deduplicate close points and merge involved edges/angles
    const merged = [];
    const radius = 5; // px slightly tighter merging to avoid losing distinct intersections
    ints.forEach((pt) => {
      let found = -1;
      for (let k = 0; k < merged.length; k += 1) {
        if (Math.hypot(merged[k].x - pt.x, merged[k].y - pt.y) < radius) { found = k; break; }
      }
      if (found >= 0) {
        const m = merged[found];
        // merge edges
        const s = new Set([...(m.edges || []), ...(pt.edges || [])]);
        m.edges = Array.from(s);
        m.angles = { ...(m.angles || {}), ...(pt.angles || {}) };
      } else {
        merged.push({ x: pt.x, y: pt.y, edges: pt.edges || [], angles: pt.angles || {} });
      }
    });
    setPoints(prev => (isSamePoints(prev, merged) ? prev : merged));
  };

  // Ensure we sample after ReactFlow updates edge paths by scheduling recompute two frames ahead.
  const recomputeNextFrame = () => {
    if (scheduledRef.current) return;
    scheduledRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scheduledRef.current = false;
        recompute();
      });
    });
  };

  // Debounced recompute on changes
  useDebouncedEffect(recomputeNextFrame, [edges, nodes], 80);

  // Ensure initial computation on mount
  useEffect(() => {
    recomputeNextFrame();
    const t = setTimeout(() => recomputeNextFrame(), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return () => clearTimeout(t);
  }, []);

  // Real-time updates while dragging (throttled by RAF + schedule flag)
  useEffect(() => {
    if (dragging) {
      const loop = () => {
        recomputeNextFrame();
        rafRef.current = requestAnimationFrame(loop);
      };
      loop();
    } else if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [dragging]);

  // Custom event listener for drag updates from Flow.js
  useEffect(() => {
    const handler = () => recomputeNextFrame();
    window.addEventListener('rf-node-drag', handler);
    return () => window.removeEventListener('rf-node-drag', handler);
  }, []);

  // MutationObserver: watch edges container for attribute and child changes (paths may be replaced during drag)
  useEffect(() => {
    const container = document.querySelector('.react-flow__edges');
    if (!container) return () => {};
    let lastDConcat = '';
    const observer = new MutationObserver(() => {
      const currentD = Array.from(container.querySelectorAll('path')).map(p => p.getAttribute('d')).join('|');
      if (currentD !== lastDConcat) {
        lastDConcat = currentD;
        recomputeNextFrame();
      }
    });
    observer.observe(container, { attributes: true, subtree: true, attributeFilter: ['d'], childList: true });
    return () => observer.disconnect();
  }, []);

  // Clear intersections if all edges are removed (e.g., flow reset/new file loaded).
  useEffect(() => {
    if (!edges || edges.length === 0) {
      cacheRef.current.clear();
      setPoints([]);
    }
  }, [edges]);

  // Custom external reset event for intersections: dispatch `window.dispatchEvent(new Event('rf-intersections-reset'))` to clear.
  useEffect(() => {
    const resetHandler = () => {
      cacheRef.current.clear();
      setPoints([]);
    };
    window.addEventListener('rf-intersections-reset', resetHandler);
    return () => window.removeEventListener('rf-intersections-reset', resetHandler);
  }, []);

  // Pointer fallback: if store.dragging is unreliable, use pointer events to drive rAF updates
  useEffect(() => {
    let active = false;
    const down = () => {
      active = true;
      const tick = () => {
        if (!active) return;
        recomputeNextFrame();
        rafRef.current = requestAnimationFrame(tick);
      };
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(tick);
    };
    const up = () => {
      active = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
    const pane = document.querySelector('.react-flow__pane');
    const target = pane || window;
    target.addEventListener('pointerdown', down);
    window.addEventListener('pointerup', up);
    return () => {
      target.removeEventListener('pointerdown', down);
      window.removeEventListener('pointerup', up);
    };
  }, []);

  if (!points.length) return null;

  return (
    <EdgeLabelRenderer>
      {points.map((p, idx) => {
        // Simple vertical marker for each intersection point.
        const size = 15; // svg viewport size
        const stroke = 'lightblue';
        const strokeWidth = 2;
        return (
          <svg
            key={idx}
            width={size}
            height={size}
            className="intersection_point"
            viewBox="-8 -8 16 16"
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${p.x}px, ${p.y}px)`,
              zIndex: 4,
              background: '#fff',
              pointerEvents: 'none',
            }}
          >
            <line
              x1={0}
              y1={-7}
              x2={0}
              y2={7}
              stroke={stroke}
              strokeWidth={strokeWidth}
              strokeLinecap="round"
            />
            {/* <path
              d="M -7 -1.3 L -7 0 A 7 7 0 0 0 7 0 L 7 -1.3"
              fill="none"
              stroke={stroke}
              strokeWidth={strokeWidth}
              strokeLinejoin="round"
            /> */}
          </svg>
        );
      })}
    </EdgeLabelRenderer>
  );
};

export default EdgeIntersections;
